import { useCallback, useEffect, useState } from "react";
import { supportService } from "../../services/support-service";

const initialForm = { subject: "", category: "Company Details Change", priority: "normal", message: "" };

function SupportTicketsPage({ title = "Support Tickets", subtitle = "Raise support requests and track admin replies" }) {
    const [tickets, setTickets] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [reply, setReply] = useState({});
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        supportService.myTickets()
            .then((data) => setTickets(data.tickets || []))
            .catch((error) => setMessage(error.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const update = (event) => setForm({ ...form, [event.target.name]: event.target.value });

    const createTicket = async (event) => {
        event.preventDefault();
        try {
            await supportService.createTicket(form);
            setForm(initialForm);
            setMessage("Support ticket created.");
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const sendReply = async (ticketId) => {
        try {
            await supportService.replyToTicket(ticketId, reply[ticketId] || "");
            setReply({ ...reply, [ticketId]: "" });
            setMessage("Reply added.");
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    return (
        <div className="twm-right-section-panel site-bg-gray">
            <div className="wt-admin-right-page-header clearfix">
                <h2>{title}</h2>
                <div className="breadcrumbs"><span>Dashboard</span><span>{subtitle}</span></div>
            </div>
            {message && <div className="alert alert-info">{message}</div>}
            <div className="alert alert-primary">
                Use support tickets to contact admin for company detail corrections, company approval follow-up, job posting issues, application queries, billing, or any portal support request.
            </div>
            <form onSubmit={createTicket} className="panel panel-default site-bg-white m-b30">
                <div className="panel-heading wt-panel-heading p-a20">
                    <h4 className="panel-tittle m-a0"><i className="fa fa-headset" />Create Ticket</h4>
                </div>
                <div className="panel-body wt-panel-body p-a20">
                    <div className="row">
                        <Field className="col-md-6" label="Subject" name="subject" value={form.subject} onChange={update} />
                        <div className="col-md-3 m-b20">
                            <label>Category</label>
                            <select className="form-control" name="category" value={form.category} onChange={update}>
                                <option>Company Details Change</option>
                                <option>Company Approval</option>
                                <option>Job Posting</option>
                                <option>Application Query</option>
                                <option>Billing</option>
                                <option>General</option>
                            </select>
                        </div>
                        <div className="col-md-3 m-b20">
                            <label>Priority</label>
                            <select className="form-control" name="priority" value={form.priority} onChange={update}>
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                        <div className="col-md-12 m-b20">
                            <label>Message</label>
                            <textarea className="form-control" name="message" rows={4} value={form.message} onChange={update} />
                        </div>
                    </div>
                    <button className="site-button" type="submit">Raise Ticket</button>
                </div>
            </form>
            <div className="panel panel-default site-bg-white">
                <div className="panel-heading wt-panel-heading p-a20 d-flex justify-content-between align-items-center">
                    <h4 className="panel-tittle m-a0"><i className="fa fa-life-ring" />My Tickets</h4>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={load} disabled={loading}>{loading ? "Syncing..." : "Refresh"}</button>
                </div>
                <div className="panel-body wt-panel-body p-a20">
                    {loading ? (
                        <div className="p-a20 text-center">Loading support tickets...</div>
                    ) : tickets.length === 0 ? (
                        <div className="p-a20 text-center">No support tickets yet.</div>
                    ) : tickets.map((ticket) => (
                        <TicketCard key={ticket.id} ticket={ticket} reply={reply[ticket.id] || ""} setReply={(value) => setReply({ ...reply, [ticket.id]: value })} sendReply={() => sendReply(ticket.id)} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function Field({ className, label, name, value, onChange }) {
    return (
        <div className={`${className} m-b20`}>
            <label>{label}</label>
            <input className="form-control" name={name} value={value} onChange={onChange} />
        </div>
    );
}

function TicketCard({ ticket, reply, setReply, sendReply }) {
    const closed = ["resolved", "closed"].includes(ticket.status);
    return (
        <div className="border rounded p-a20 m-b20 bg-white">
            <div className="d-flex justify-content-between flex-wrap">
                <div>
                    <h4 className="m-b5">{ticket.subject}</h4>
                    <div className="text-muted">{ticket.category} | {ticket.priority} priority | {formatDate(ticket.created_at)}</div>
                </div>
                <span className={`badge ${closed ? "bg-success" : "bg-warning text-dark"}`}>{ticket.status?.replace(/_/g, " ")}</span>
            </div>
            <div className="m-t20">
                {(ticket.messages || []).map((item) => (
                    <div key={item.id} className={`p-a15 m-b10 ${item.sender_role === "admin" ? "site-bg-gray" : "bg-light"}`}>
                        <strong>{item.sender_role === "admin" ? "Zozo Jobs Support" : "You"}</strong>
                        <span className="text-muted ms-2">{formatDate(item.created_at)}</span>
                        <div>{item.message}</div>
                    </div>
                ))}
            </div>
            {!closed && (
                <div className="row">
                    <div className="col-md-10">
                        <textarea className="form-control" rows={2} value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Add a reply" />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <button className="site-button w-100" type="button" onClick={sendReply}>Reply</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function formatDate(value) {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export default SupportTicketsPage;
